import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Save, Loader2, LogOut, CheckCircle2, AlertCircle } from 'lucide-react';
import { settingsApi } from '../../lib/api';
import type { ExitStrategyResponse } from '../../types/titan';

/** Friendly labels for the dial env-var names shown in each profile card. */
const DIAL_LABELS: Record<string, string> = {
  TITAN_RUNNER_LADDER: 'Partial-profit ladder',
  TITAN_TP_LADDER_PROFILE: 'Ladder shape',
  TITAN_PROFIT_RATCHET_MODE: 'Profit ratchet',
  TITAN_PROFIT_RATCHET_ARM_R: 'Ratchet arms at (R)',
  TITAN_PROFIT_GIVEBACK_PCT: 'Max giveback of peak gain (%)',
  TITAN_LOSER_TIMEBOX_MODE: 'Loser timebox',
  TITAN_LOSER_TIMEBOX_MINUTES: 'Timebox after (min)',
  TITAN_LOSER_TIMEBOX_R: 'Timebox if worse than (R)',
  TITAN_SL_ATR_MULT_STOCKS: 'Stop distance (× ATR)',
  TITAN_SL_MAX_PCT_STOCKS: 'Max stop distance (%)',
  TITAN_STALL_CHECK_MINUTES: 'Stall exit window (min)',
};

export default function ExitStrategyTab() {
  const [data, setData] = useState<ExitStrategyResponse | null>(null);
  const [selected, setSelected] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const res = await settingsApi.exitStrategy();
      setData(res);
      setSelected(res?.settings?.profile || res?.default_profile || 'harvest');
    } catch {
      setError('Failed to load exit-strategy settings');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!selected) return;
    setSaving(true);
    setMsg('');
    setError('');
    try {
      await settingsApi.saveExitStrategy(selected);
      setMsg(`Exit strategy set to ${selected}. Live within ~60 seconds — open positions switch too.`);
      setTimeout(() => setMsg(''), 6000);
      await load();
    } catch (e: any) {
      setError(e?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return (
    <div className="flex items-center gap-2 text-muted-foreground py-8">
      <Loader2 className="w-4 h-4 animate-spin" /> Loading exit-strategy settings…
    </div>
  );

  if (!data) return (
    <div className="flex items-center gap-2 text-red-500 py-8">
      <AlertCircle className="w-4 h-4" /> {error || 'Exit-strategy settings unavailable'}
    </div>
  );

  const envOverrides = Object.entries(data.env_overrides || {});
  const dirty = selected !== data.settings?.profile;

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <LogOut className="w-4 h-4" /> Exit Strategy
          </CardTitle>
          <CardDescription>
            How positions take profit and cut losses. Switching applies to the live engine
            (and open positions) within about a minute — no restart needed.
          </CardDescription>
        </CardHeader>
        <CardContent className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {Object.entries(data.profiles || {}).map(([name, profile]) => {
            const isSelected = selected === name;
            const isActive = data.active_profile === name;
            return (
              <button
                key={name}
                type="button"
                onClick={() => setSelected(name)}
                className={`text-left rounded-lg border p-4 transition-colors space-y-2 ${
                  isSelected ? 'border-primary bg-primary/5' : 'border-muted hover:border-primary/40'
                }`}
              >
                <div className="flex items-center justify-between">
                  <span className="font-medium">{profile.label}</span>
                  <span className="flex items-center gap-1">
                    {name === data.default_profile && (
                      <Badge variant="outline" className="text-[10px]">default</Badge>
                    )}
                    {isActive && (
                      <Badge className="text-[10px]">active</Badge>
                    )}
                  </span>
                </div>
                <p className="text-xs text-muted-foreground">{profile.description}</p>
                <div className="pt-1 space-y-0.5">
                  {Object.entries(profile.dials).map(([env, value]) => (
                    <div key={env} className="flex items-center justify-between text-[11px]">
                      <span className="text-muted-foreground">{DIAL_LABELS[env] ?? env}</span>
                      <span className="font-mono">
                        {value}
                        {env in (data.env_overrides || {}) && (
                          <span className="text-amber-500 ml-1" title="Pinned by server env — overrides the profile">*</span>
                        )}
                      </span>
                    </div>
                  ))}
                </div>
              </button>
            );
          })}
        </CardContent>
      </Card>

      {envOverrides.length > 0 && (
        <Card className="border-amber-500/30 bg-amber-500/5">
          <CardContent className="pt-4 pb-3 text-xs text-muted-foreground">
            <span className="font-medium text-foreground">Server overrides active:</span>{' '}
            {envOverrides.map(([env]) => DIAL_LABELS[env] ?? env).join(', ')} — these dials are
            pinned in the server environment and win over any profile until unset.
          </CardContent>
        </Card>
      )}

      <div className="flex items-center gap-4">
        <Button onClick={save} disabled={saving || !dirty} className="flex items-center gap-2">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          Apply Exit Strategy
        </Button>
        {msg && (
          <span className="text-sm text-green-600 flex items-center gap-1">
            <CheckCircle2 className="w-3 h-3" /> {msg}
          </span>
        )}
        {error && <span className="text-sm text-red-500">{error}</span>}
      </div>
    </div>
  );
}
